import type {
  SlashMenuActionItem,
  SlashMenuConfig,
  SlashMenuContext,
  SlashMenuItem,
  SlashMenuSubMenu,
} from './types';

export function isActionItem(item: SlashMenuItem): item is SlashMenuActionItem {
  return 'action' in item;
}

export function isSubMenuItem(item: SlashMenuItem): item is SlashMenuSubMenu {
  return 'subMenu' in item;
}

export function slashItemClassName({ name }: SlashMenuItem) {
  return name.split(' ').join('-').toLocaleLowerCase();
}

export function getSlashMenuSearchTexts(
  item: SlashMenuItem,
  translate: (kind: 'item' | 'description', fallback: string) => string = (
    _kind,
    fallback
  ) => fallback
) {
  const texts = new Set<string>([item.name]);
  const translatedName = translate('item', item.name);

  if (translatedName !== item.name) texts.add(translatedName);

  for (const alias of item.searchAlias ?? []) {
    texts.add(alias);
    const translatedAlias = translate('item', alias);
    if (translatedAlias !== alias) texts.add(translatedAlias);
  }

  if (item.description) {
    texts.add(item.description);
    const translatedDescription = translate('description', item.description);
    if (translatedDescription !== item.description)
      texts.add(translatedDescription);
  }

  return [...texts];
}

export function translateSlashMenuText(
  kind: 'item' | 'description' | 'tooltip' | 'group',
  fallback: string,
  translate: (
    kind: 'item' | 'description' | 'tooltip' | 'group',
    fallback: string
  ) => string,
  options?: {
    disambiguator?: string;
    ordinal?: number;
  }
) {
  // Dynamic surface references keep the user-provided title after a stable
  // type prefix, so translate the prefix without inventing a key per title.
  const dynamicPrefix =
    kind === 'item' ? /^(Frame|Group): (.*)$/u.exec(fallback) : null;
  const title = dynamicPrefix?.[2];
  const generatedReferenceType =
    dynamicPrefix && isGeneratedSurfaceReferenceName(fallback)
      ? dynamicPrefix[1]
      : null;

  // Generated Frame and Group titles are internal identifiers, not useful
  // action labels. Menu order gives reference actions a stable, visible ordinal
  // without exposing a model ID or generated title.
  if (generatedReferenceType) {
    const referenceLabel = translate(
      'item',
      generatedReferenceType === 'Frame'
        ? 'Reference frames'
        : 'Reference groups'
    );
    const ordinal = options?.ordinal;
    const disambiguator = options?.disambiguator?.trim();
    if (ordinal && ordinal > 0) return `${referenceLabel} ${ordinal}`;
    if (disambiguator && !disambiguator.startsWith('#')) {
      return `${referenceLabel} · ${disambiguator}`;
    }
    return `${referenceLabel} 1`;
  }

  const translated = translate(kind, fallback);
  if (translated !== fallback || kind !== 'item') return translated;
  if (!dynamicPrefix || !title) return fallback;

  const translatedPrefix = translate('item', dynamicPrefix[1]);

  return translatedPrefix === dynamicPrefix[1]
    ? fallback
    : `${translatedPrefix}: ${title}`;
}

export function isGeneratedSurfaceReferenceName(name: string) {
  const match = /^(Frame|Group): (Frame|Group) \d+(?: \(\d+\))?$/u.exec(name);
  return !!match && match[1] === match[2];
}

export function isGeneratedGroupHeader(groupName: string) {
  return /^Group: Group \d+(?: \(\d+\))?$/u.test(groupName);
}

export function parseGroup(group: NonNullable<SlashMenuItem['group']>) {
  return [
    parseInt(group.split('_')[0]),
    group.split('_')[1].split('@')[0],
    parseInt(group.split('@')[1]),
  ] as const;
}

function itemCompareFn(a: SlashMenuItem, b: SlashMenuItem) {
  if (a.group === undefined && b.group === undefined) return 0;
  if (a.group === undefined) return -1;
  if (b.group === undefined) return 1;

  const [aGroupIndex, aGroupName, aItemIndex] = parseGroup(a.group);
  const [bGroupIndex, bGroupName, bItemIndex] = parseGroup(b.group);
  if (isNaN(aGroupIndex)) return -1;
  if (isNaN(bGroupIndex)) return 1;
  if (aGroupIndex < bGroupIndex) return -1;
  if (aGroupIndex > bGroupIndex) return 1;

  if (aGroupName !== bGroupName) return aGroupName.localeCompare(bGroupName);

  if (isNaN(aItemIndex)) return -1;
  if (isNaN(bItemIndex)) return 1;

  return aItemIndex - bItemIndex;
}

export function buildSlashMenuItems(
  items: SlashMenuItem[],
  context: SlashMenuContext,
  transform?: (item: SlashMenuItem) => SlashMenuItem
): SlashMenuItem[] {
  if (transform) items = items.map(transform);

  const result = items
    .filter(item => (item.when ? item.when(context) : true))
    .sort(itemCompareFn)
    .map(item => {
      if (isSubMenuItem(item)) {
        return {
          ...item,
          subMenu: buildSlashMenuItems(item.subMenu, context),
        };
      } else {
        return { ...item };
      }
    });
  return result;
}

export function mergeSlashMenuConfigs(
  configs: Map<string, SlashMenuConfig>
): SlashMenuConfig {
  return {
    items: ctx =>
      Array.from(configs.values()).flatMap(({ items }) =>
        typeof items === 'function' ? items(ctx) : items
      ),
    disableWhen: ctx =>
      configs
        .values()
        .map(({ disableWhen }) => disableWhen?.(ctx) ?? false)
        .some(Boolean),
  };
}

export function formatDate(date: Date) {
  // yyyy-mm-dd
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const strTime = `${year}-${month}-${day}`;
  return strTime;
}

export function formatTime(date: Date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const strTime = `${formatDate(date)} ${hours}:${minutes}`;
  return strTime;
}
